import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { MasonryPhotoAlbum } from 'react-photo-album'
import 'react-photo-album/masonry.css'

import PageSection from '../../../../features/components/PageSection'
import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import AdsGalleryItem from './AdsGalleryItem'
import SkeletonAdsGallery from './Skeleton'

import {
  adsErrorSelector,
  adsIsLoadingSelector,
  adsNextSelector,
  adsWasLoadedSelector
} from '../../../../modules/selectors/ads'

import { phonesDownWidth, tabletDownWidth } from '../../../../styles/const/breakpoints'

import useStyles from './styles'

// this component is used for DV360 ads list representation
const AdsGallery = ({
  adsList,
  noDataText,
  showDetails,
  showActionsDropdown,
  showInternalAdActionButtons,
  loadMoreAdsHandler,
  disapproveInternalAdOpenModalHandler
}) => {
  const classes = useStyles()

  const adsNext = useSelector(adsNextSelector)

  const photos = useMemo(
    () =>
      adsList.map(ad => ({
        src: ad.assets?.[0].asset.url,
        name: ad.name,
        // default to 1 as a fallback if width is not defined
        width: ad.dimensions?.['width_pixels'] || 1,
        height: ad.dimensions?.['height_pixels'] || 1,
        ...ad
      })),
    [adsList]
  )

  const albumColumns = useCallback(containerWidth => {
    if (containerWidth < 410) return 1
    if (containerWidth < phonesDownWidth) return 2
    if (containerWidth < tabletDownWidth) return 3
    return 4
  }, [])

  const EnhancedAdsGalleryItem = useCallback(
    (id, props) => {
      // renderDefaultPhoto

      return (
        <AdsGalleryItem
          showDetails={showDetails}
          showActionsDropdown={showActionsDropdown}
          showInternalAdActionButtons={showInternalAdActionButtons}
          disapproveInternalAdOpenModalHandler={disapproveInternalAdOpenModalHandler}
          {...props}
        />
      )
    },
    [showDetails, showActionsDropdown, showInternalAdActionButtons, disapproveInternalAdOpenModalHandler]
  )

  const skeletonProps = useMemo(
    () => ({
      columns: albumColumns,
      showDetails,
      showInternalAdActionButtonsSkeleton: showInternalAdActionButtons
    }),
    [albumColumns, showDetails, showInternalAdActionButtons]
  )

  return (
    <ItemsLoading
      className={classes.gallery}
      noDataText={noDataText}
      itemsLength={adsList.length}
      SkeletonComponent={SkeletonAdsGallery}
      initialLoadingSkeletonsNumber={1}
      loadingMoreSkeletonsNumber={1}
      skeletonProps={skeletonProps}
      errorSelector={adsErrorSelector}
      isLoadingSelector={adsIsLoadingSelector}
      wasLoadedSelector={adsWasLoadedSelector}
      ContentWrapper={PageSection}
      loadMore={adsNext ? loadMoreAdsHandler : null}
    >
      <MasonryPhotoAlbum
        photos={photos}
        spacing={16}
        columns={albumColumns}
        render={{ photo: EnhancedAdsGalleryItem }}
      />
    </ItemsLoading>
  )
}

AdsGallery.propTypes = {
  adsList: PropTypes.array.isRequired,
  noDataText: PropTypes.string,
  showDetails: PropTypes.bool,
  showActionsDropdown: PropTypes.bool,
  showInternalAdActionButtons: PropTypes.bool,
  loadMoreAdsHandler: PropTypes.func,
  disapproveInternalAdOpenModalHandler: PropTypes.func
}

export default AdsGallery
